import { useState, useEffect } from 'react';

import { cn } from '@/lib/utils';
import { useAnnouncement } from '@/hooks/useAnnouncement';
import type { AnnouncementSeverity } from '@/lib/api/announcement';

const DISMISSED_KEY_PREFIX = 'announcement-dismissed:';

// Stable short hash (djb2) — used to key a dismissal to the message content, so
// dismissing hides *this* message but any edit to it re-shows for everyone.
function hashContent(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
}

// Per-severity palette (background + foreground). info/error use white text on a
// saturated fill. Warning is the exception: amber is too light for legible white
// text, and darkening it enough turns it into muddy orange — so warning uses a
// bright amber with near-black text (amber-950), which is both highly readable and
// unmistakably amber. Each severity owns its message/button tones so they stay in sync.
const SEVERITY_STYLES: Record<
  AnnouncementSeverity,
  { container: string; message: string; button: string }
> = {
  info: {
    container: 'bg-blue-500 dark:bg-blue-600 text-white',
    message: 'text-white/85',
    button: 'border-white/40 text-white hover:bg-white/10',
  },
  warning: {
    container: 'bg-amber-400 dark:bg-amber-500 text-amber-950',
    message: 'text-amber-950/80',
    button: 'border-amber-950/30 text-amber-950 hover:bg-amber-950/10',
  },
  error: {
    container: 'bg-red-500 dark:bg-red-600 text-white',
    message: 'text-white/85',
    button: 'border-white/40 text-white hover:bg-white/10',
  },
};

// Presentational banner — shared by the live banner and the admin preview so
// the two can never drift. Centered title + message (no icon), Close button on
// the right; a matching left spacer keeps the message optically centered.
export function AnnouncementBannerView({
  severity,
  title,
  message,
  dismissible = true,
  onDismiss,
  className,
}: {
  severity: AnnouncementSeverity;
  title?: string;
  message?: string;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}): React.ReactElement {
  const styles = SEVERITY_STYLES[severity];

  return (
    <div className={cn('px-4 py-2', styles.container, className)}>
      <div className="flex items-center gap-3">
        {/* Left spacer balances the Close button so the message stays centered
            on desktop; on mobile the message left-aligns and grows instead. */}
        <div className="hidden flex-1 sm:block" aria-hidden="true" />
        {/* sm:flex-initial (not flex-none) so a long message wraps inside the
            available width instead of widening the row and pushing Close out. */}
        <div className="flex min-w-0 flex-1 flex-col text-left leading-tight sm:flex-initial sm:text-center">
          {title && (
            <span className="text-sm sm:text-[15px] font-bold tracking-tight leading-snug break-words">
              {title}
            </span>
          )}
          {message && (
            <span className={cn('text-xs sm:text-sm leading-snug break-words', styles.message)}>
              {message}
            </span>
          )}
        </div>
        <div className="flex flex-none justify-end sm:flex-1">
          {dismissible && (
            <button
              type="button"
              onClick={onDismiss}
              className={cn(
                'flex-shrink-0 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                styles.button,
              )}
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AnnouncementBanner(): React.ReactElement | null {
  const { config, loading, isActive } = useAnnouncement();
  const [dismissed, setDismissed] = useState(false);

  // Key the dismissal to the content (or an explicit id override) so editing
  // the message auto-resets dismissals — no manual "bump the id" step, and no
  // "default" collision where a new message stays hidden for past dismissers.
  const announcementId =
    config.id?.trim() ||
    hashContent(
      `${config.severity ?? 'warning'}|${config.title ?? ''}|${config.message ?? ''}`,
    );
  const storageKey = `${DISMISSED_KEY_PREFIX}${announcementId}`;
  const dismissible = config.dismissible !== false;

  useEffect(() => {
    if (!isActive || !dismissible) {
      setDismissed(false);
      return;
    }
    setDismissed(localStorage.getItem(storageKey) === '1');
  }, [isActive, dismissible, storageKey]);

  if (loading || !isActive || (dismissed && dismissible)) return null;
  if (!config.title && !config.message) return null;

  const severity: AnnouncementSeverity = config.severity ?? 'warning';

  const handleDismiss = (): void => {
    localStorage.setItem(storageKey, '1');
    setDismissed(true);
  };

  return (
    <AnnouncementBannerView
      severity={severity}
      title={config.title}
      message={config.message}
      dismissible={dismissible}
      onDismiss={dismissible ? handleDismiss : undefined}
    />
  );
}
